import type { TrendData, TrendDataPoint, TrendStatistics } from "./types";

/**
 * Patient-facing duplicate-row collapse.
 *
 * The Trends row identity is the biomarker code key (`<LOINC>`, `loinc-unit::…`,
 * `text::…`), so one real analyte fragments into several rows whenever the parser
 * assigns different LOINC codes (incl. invalid ones like a mistyped check digit)
 * or different unit strings for the same test. Patients then see the same result
 * two or three times — identical name, identical value — which reads as broken.
 *
 * This collapses `Record<string, TrendData>` entries that a patient cannot tell
 * apart — SAME normalized display name AND SAME normalized unit — into one row,
 * unioning their observations. It deliberately trusts the human-facing identity
 * (name + unit), NOT the code, so it is robust to any code the parser mangles.
 *
 * What it must NOT do: merge genuinely different measurements that happen to share
 * a name. Those differ by unit — e.g. Lp(a) in `nmol/L` (molar) vs `mg/dL` (mass) —
 * and a differing normalized unit keeps them as separate rows, which is correct.
 *
 * Apply on PATIENT surfaces only (patient web + mobile). The clinician table stays
 * per-(LOINC, unit) so clinicians can still see and merge the fragments.
 *
 * No-LOINC biomarkers (a `text::…` key) are collapsed here exactly like coded ones,
 * because grouping is by name + unit and ignores the key. That IS the de-dup policy for
 * them (ENG-5258): on patient surfaces a no-LOINC duplicate that reads identically (same
 * normalized name + unit) folds into one row. There is deliberately NO clinician-side
 * fallback for these — the biomarker merge tool (`deriveTrendsBiomarkerMergeScope`) is
 * LOINC-only and rejects a no-LOINC row, so a clinician cannot merge no-LOINC duplicates.
 * Making merge LOINC-optional is a planned follow-up (ENG-5274 — gated on a clinician
 * unit-confirmation dialog, since without a LOINC the unit is the only thing tying two rows
 * to the same measurement); until then the patient-facing collapse below is the whole
 * mitigation, and the clinician table keeps the raw fragments as-is.
 *
 * Known limitations (all display-only, so reversible by reverting — never corrupts data):
 *   - Genuine name collision: two DIFFERENT analytes that share a display name AND unit
 *     (e.g. "DHA" = docosahexaenoic acid vs dehydroascorbate) would merge into one line.
 *     Rare in practice because distinct analytes usually resolve to distinct labels.
 *   - Convertible-scale units do NOT merge (e.g. cells/uL vs x10E3/uL): merging would
 *     require rescaling a value, which a display collapse must never do silently.
 *   - Same concept under two different NAMES does not merge (grouping is by name).
 */

/** Fold a display name for concept matching: locale-aware lowercase, collapse whitespace,
 *  drop trailing lab qualifiers like "*NJ1". Null-safe: a nullish name folds to "" (an
 *  entry with no display name is never "the same concept" as another — the caller's guard
 *  passes it through untouched rather than merging on a blank key). */
export function normalizeConceptName(name: string | undefined): string {
  if (!name) return "";
  return name
    .replace(/\s*\*.*$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase();
}

/** Fold a unit to its canonical display form so format-only variants match:
 *  `% by wt`≡`%`, `µg`≡`ug`, `[IU]/mL`≡`IU/mL`, `ulU`≡`uIU`. Genuinely different
 *  units (`nmol/L` vs `mg/dL`) still differ, so their rows stay separate. */
export function normalizeConceptUnit(unit: string | undefined): string {
  if (!unit) return "";
  let u = unit.replace(/ /g, " ").trim();
  u = u.replace(/[µμ]/g, "u"); // micro sign + greek mu → u
  u = u.replace(/\[iu\]/gi, "iu"); // strip UCUM brackets around International Unit
  u = u.replace(/\bulU\b/g, "uIU"); // OCR: lowercase-L → capital-I
  u = u.replace(/^%\s*by\s*w(?:t|eight)$/i, "%");
  return u.replace(/\s+/g, " ").trim().toLocaleLowerCase();
}

function conceptKey(data: TrendData): string {
  return `${normalizeConceptName(data.metadata.displayName)}\u0000${normalizeConceptUnit(data.metadata.unit)}`;
}

function dayKey(date: string): string {
  return date.includes("T") ? (date.split("T")[0] ?? date) : date;
}

/**
 * One observation per calendar day across all fragments. `fragments` is passed
 * representative-first, so on a same-day collision the representative fragment's
 * value wins — deterministic, not dependent on Object key order. (A distinct
 * same-day value from another fragment is dropped; that's correct for the
 * duplicate case this collapses, and genuinely different same-day readings for
 * one biomarker+unit are rare.)
 */
function unionObservationsByDay(fragments: TrendData[]): TrendDataPoint[] {
  const byDay = new Map<string, TrendDataPoint>();
  for (const g of fragments) {
    for (const o of g.observations) {
      const day = dayKey(o.date);
      if (!byDay.has(day)) byDay.set(day, o);
    }
  }
  return [...byDay.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Union the per-source series across fragments so a merged biomarker keeps EVERY
 * source — the source dropdown and the clinic-vs-wearable split read `bySource`,
 * so keeping only the representative fragment's would drop a source and desync it
 * from the merged `observations`. Deduped per provider by day, representative-first.
 * Returns undefined when no fragment carried `bySource`.
 */
function unionBySource(fragments: TrendData[]): Record<string, TrendDataPoint[]> | undefined {
  const merged: Record<string, TrendDataPoint[]> = {};
  let seenAny = false;
  for (const g of fragments) {
    if (!g.bySource) continue;
    seenAny = true;
    for (const [slug, series] of Object.entries(g.bySource)) {
      const bucket = merged[slug] ?? [];
      merged[slug] = bucket;
      const days = new Set(bucket.map((o) => dayKey(o.date)));
      for (const o of series) {
        const d = dayKey(o.date);
        if (days.has(d)) continue;
        bucket.push(o);
        days.add(d);
      }
    }
  }
  if (!seenAny) return undefined;
  for (const series of Object.values(merged)) series.sort((a, b) => a.date.localeCompare(b.date));
  return merged;
}

/** Core statistics over merged observations — pure + client-safe. `current` = most recent,
 *  plus a half-vs-half trend (5% band = "stable"). NOT identical to the server's trend rule
 *  (the server compares a fixed 7-day recent window vs the prior 7 days); a merged row's arrow
 *  can therefore differ from an un-merged row's. Only the fields it returns are recomputed —
 *  the caller spreads the representative's `statistics` first, so server-provided extras like
 *  `percentChange` / `previousValues` survive on merged rows. */
function computeStatistics(observations: TrendDataPoint[]): TrendStatistics {
  if (observations.length === 0) {
    return { current: 0, average: 0, min: 0, max: 0, count: 0, trend: "stable" };
  }
  const byRecent = [...observations].sort((a, b) => b.date.localeCompare(a.date));
  const values = observations.map((o) => o.value);
  const current = byRecent[0]?.value ?? 0;
  const average = values.reduce((a, b) => a + b, 0) / values.length;

  let trend: TrendStatistics["trend"] = "stable";
  if (byRecent.length >= 2) {
    const mid = Math.ceil(byRecent.length / 2);
    const recentAvg = byRecent.slice(0, mid).reduce((s, o) => s + o.value, 0) / mid;
    const older = byRecent.slice(mid);
    const olderAvg = older.length
      ? older.reduce((s, o) => s + o.value, 0) / older.length
      : recentAvg;
    const delta = recentAvg - olderAvg;
    const threshold = Math.abs(olderAvg) * 0.05; // 5% band = "stable"
    if (delta > threshold) trend = "increasing";
    else if (delta < -threshold) trend = "decreasing";
  }

  return {
    current,
    average,
    min: Math.min(...values),
    max: Math.max(...values),
    count: observations.length,
    trend,
  };
}

/**
 * Collapse patient-indistinguishable rows (same name + same unit) into one, unioning
 * their observations. Single-member concepts pass through untouched. The representative
 * entry (most observations, tie-break shortest key) keeps its key/metadata/range so
 * downstream keying stays stable.
 */
export function collapseDuplicateTrendData(
  data: Record<string, TrendData>,
): Record<string, TrendData> {
  const groups = new Map<string, Array<[string, TrendData]>>();
  const result: Record<string, TrendData> = {};
  for (const entry of Object.entries(data)) {
    // Pass through untouched (don't merge) any entry we can't key as a real concept:
    // missing metadata (would crash conceptKey), or a blank display name (an empty name
    // is not "the same biomarker" — merging on it would collapse unrelated rows).
    if (!entry[1]?.metadata || !normalizeConceptName(entry[1].metadata.displayName)) {
      result[entry[0]] = entry[1];
      continue;
    }
    const key = conceptKey(entry[1]);
    const bucket = groups.get(key);
    if (bucket) bucket.push(entry);
    else groups.set(key, [entry]);
  }

  for (const bucket of groups.values()) {
    if (bucket.length === 1) {
      const [k, v] = bucket[0];
      result[k] = v;
      continue;
    }
    // Representative-first order (most observations, tie-break shortest key). Drives
    // BOTH the representative choice and same-day/same-source collision resolution.
    const ordered = [...bucket].sort(
      (a, b) => b[1].observations.length - a[1].observations.length || a[0].length - b[0].length,
    );
    const [repKey, repData] = ordered[0];
    const fragments = ordered.map(([, v]) => v);
    const merged = unionObservationsByDay(fragments);
    const bySource = unionBySource(fragments);
    result[repKey] = {
      ...repData,
      observations: merged,
      // Spread the representative's statistics first so server-only extras
      // (percentChange, rolling30DayAvg, previousValues) survive; then override the
      // core fields with values recomputed over the unioned observations.
      statistics: { ...repData.statistics, ...computeStatistics(merged) },
      ...(bySource ? { bySource } : {}),
    };
  }
  return result;
}
